import { v4 as uuidv4 } from 'uuid';
import { Document, DocumentType } from '../types/dataRoom';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import apiService from '../services/apiService';

export const createDocument = (
  file: File,
  type: DocumentType,
  directorId?: string,
  tags: string[] = []
): Document => {
  // Debug logging

  // Generate default tags based on document type if not provided
  const defaultTags = tags.length > 0 ? tags : [formatDocumentType(type)];

  return {
    id: uuidv4(),
    name: file.name,
    type,
    uploadedAt: new Date(),
    directorId,
    tags: defaultTags,
    isSelected: false
  };
};

export const formatDocumentType = (type: DocumentType): string => {
  switch (type) {
    case 'audited_financials_2022_23':
      return 'Audited Financials 2022-23';
    case 'audited_financials_2023_24':
      return 'Audited Financials 2023-24';
    case 'audited_financials_2024_25':
      return 'Audited Financials 2024-25';
    case 'provisional_financials':
      return 'Provisional Financials';
    case 'mis_financials':
      return 'MIS';
    case 'bank_statements':
      return 'Bank Statements';
    case 'gst_filing':
      return 'GST Filing';
    case 'company_kyc':
      return 'Company KYC Documents';
    case 'director_kyc':
      return 'Director KYC Documents';
    case 'company_deck':
      return 'Company Deck';
    case 'other':
      return 'Other';
    case 'asset_leasing':
      return 'Asset Leasing';
    case 'sanction_letter':
      return 'Sanction Letter';
    case 'itr':
      return 'ITR';
    default:
      return 'Unknown';
  }
};

export const guessDocumentType = (fileName: string): DocumentType => {
  const lowerFileName = fileName.toLowerCase();
    
  // Financial document detection
  if (lowerFileName.includes('financials') || lowerFileName.includes('audit')) {
    if (lowerFileName.includes('2022') || lowerFileName.includes('22-23')) {
      return 'audited_financials_2022_23';
    }
    if (lowerFileName.includes('2023') || lowerFileName.includes('23-24')) {
      return 'audited_financials_2023_24';
    }
    if (lowerFileName.includes('2024') || lowerFileName.includes('24-25')) {
      return 'audited_financials_2024_25';
    }
    if (lowerFileName.includes('provisional')) {
      return 'provisional_financials';
    }
    if (lowerFileName.includes('mis')) {
      return 'mis_financials';
    }
    return 'audited_financials_2024_25';
  }
  
  // Banking and GST
  if (lowerFileName.includes('bank') || lowerFileName.includes('statement')) {
    return 'bank_statements';
  }
  if (lowerFileName.includes('gst') || lowerFileName.includes('filing')) {
    return 'gst_filing';
  }
  
  // KYC Documents
  if (lowerFileName.includes('kyc')) {
    if (lowerFileName.includes('director')) {
      return 'director_kyc';
    }
    return 'company_kyc';
  }
  
  // Company Deck
  if (lowerFileName.includes('deck') || lowerFileName.includes('presentation')) {
    return 'company_deck';
  }
  return 'other';
};

export const downloadDocuments = async (documents: Document[]): Promise<void> => {
  if (documents.length === 0) return;
  
  if (documents.length === 1) {
    // Single file download
    const doc = documents[0];
    if (doc.url) {
      try {
        const file = await urlToFile(doc.url, doc.name);
        saveAs(file, doc.name);
      } catch (error) {
        console.error(`Failed to download ${doc.name}`, error);
        alert('Failed to download the document. Please try again.');
      }
      return;
    }
  }
  
  // Multiple files - create a ZIP with folder structure
  try {
    const zip = new JSZip();
    const companyFolder = zip.folder("Company");
    
    if (!companyFolder) {
      throw new Error("Failed to create Company folder");
    }
    
    // Group documents by category
    const documentsByCategory = documents.reduce((acc, doc) => {
      const category = getDocumentCategory(doc.type);
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(doc);
      return acc;
    }, {} as Record<string, Document[]>);
    
    // Add files to ZIP in appropriate folders
    const promises: Promise<void>[] = [];
    
    Object.entries(documentsByCategory).forEach(([category, docs]) => {
      const folder = companyFolder.folder(category);
      
      if (folder) {
        docs.forEach(doc => {
          if (doc.url) {
            const promise = urlToFile(doc.url,doc.name)
              .then((res) => res.arrayBuffer())
              .then((buffer) => {
                folder.file(doc.name, buffer); // add to zip
              })
              .catch((err) => {
                console.error(`Failed to fetch ${doc.name}`, err);
              });
      
            promises.push(promise);
          }
        });
      }
    });
    
    await Promise.all(promises);
    
    // Generate and download the ZIP
    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'Company_Documents.zip');
    
  } catch (error) {
    console.error('Error creating ZIP file:', error);
    alert('An error occurred while preparing the download. Please try again.');
  }
};

async function urlToFile(url: string, fileName: string): Promise<File> {
  const response = await apiService.post(
    "/data-room/get-file",
    { url },
    { responseType: 'blob' }
  );

  const blob = response as Blob;
  const type = blob.type || 'application/octet-stream';
  return new File([blob], fileName, { type });
}

export const filterDocumentsByType = (
  documents: Document[],
  type?: DocumentType,
  directorId?: string
): Document[] => {
  return documents.filter(doc => {
    const typeMatch = type ? doc.type === type : true;
    const directorMatch = directorId !== undefined ? doc.directorId === directorId : true;
    return typeMatch && directorMatch;
  });
};

// Simulate uploading a file to a server
export const uploadFile = async (file: File): Promise<string> => {
  return new Promise((resolve) => {
    // Simulate network delay
    setTimeout(() => {
      // In a real implementation, this would upload to a server and return a URL
      resolve(URL.createObjectURL(file));
    }, 1000);
  });
};

// Get document category display name from document type
export const getDocumentCategory = (type: DocumentType): string => {
  switch(type) {
    case 'audited_financials_2022_23':
    case 'audited_financials_2023_24':
    case 'audited_financials_2024_25':
    case 'provisional_financials':
    case 'mis_financials':
      case 'itr':
      return 'Financials';
    case 'bank_statements':
      case 'sanction_letter':
    case 'gst_filing':
      return 'Banking';
    case 'company_kyc':
    case 'director_kyc':
    case 'company_deck':
      return 'KYC Documents';
    default:
      return 'Other';
  }
};
